import { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import api from '../lib/api';
import { Bell, MessageCircle } from 'lucide-react';
import { Button } from './ui/button';

const POLL_INTERVAL = 60000; // 60s light poll

export default function TopBarIcons() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [chatUnread, setChatUnread] = useState(0);
  const [notifUnread, setNotifUnread] = useState(0);
  const intervalRef = useRef(null);

  const fetchCounts = useCallback(async () => {
    if (!user) return;
    try {
      const chatRes = await api.get('/chat/unread-count');
      setChatUnread(chatRes.data.unread_count || 0);
    } catch { /* ignore */ }

    // User-notifications: available to ALL roles
    try {
      const notifRes = await api.get('/user-notifications/unread-count');
      setNotifUnread(notifRes.data.unread_count || 0);
    } catch { /* ignore */ }
  }, [user]);

  useEffect(() => {
    fetchCounts();
    intervalRef.current = setInterval(fetchCounts, POLL_INTERVAL);
    return () => clearInterval(intervalRef.current);
  }, [fetchCounts]);

  useEffect(() => {
    window.__refreshTopBarCounts = fetchCounts;
    return () => { delete window.__refreshTopBarCounts; };
  }, [fetchCounts]);

  return (
    <div className="flex items-center gap-1" data-testid="topbar-icons">
      <Button
        variant="ghost"
        size="icon"
        className="relative text-muted-foreground hover:text-foreground"
        onClick={() => navigate('/chat')}
        title={t('nav.chat')}
        data-testid="topbar-chat-btn"
      >
        <MessageCircle className="w-5 h-5" />
        {chatUnread > 0 && (
          <span className="absolute -top-0.5 -end-0.5 min-w-[18px] h-[18px] flex items-center justify-center rounded-full bg-red-500 text-white text-[10px] font-bold px-1 leading-none" data-testid="topbar-chat-badge">
            {chatUnread > 99 ? '99+' : chatUnread}
          </span>
        )}
      </Button>

      <Button
        variant="ghost"
        size="icon"
        className="relative text-muted-foreground hover:text-foreground"
        onClick={() => navigate('/notifications')}
        title={t('nav.notifications')}
        data-testid="topbar-notif-btn"
      >
        <Bell className="w-5 h-5" />
        {notifUnread > 0 && (
          <span className="absolute -top-0.5 -end-0.5 min-w-[18px] h-[18px] flex items-center justify-center rounded-full bg-red-500 text-white text-[10px] font-bold px-1 leading-none" data-testid="topbar-notif-badge">
            {notifUnread > 99 ? '99+' : notifUnread}
          </span>
        )}
      </Button>
    </div>
  );
}
